<template>
  <div ref="root" class="ak-modal-container">
    <transition
      :name="animation"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
    >
      <div
        v-if="isActive"
        v-trap-focus="trapFocus"
        :role="ariaRole"
        :aria-modal="ariaModal"
        :style="zIndex"
        class="ak-modal ak-media-manager"
      >
        <transition :name="animation">
          <div
            class="ak-modal__dialog ak-media-manager__dialog"
            @dragover="dragover"
            @dragleave="dragleave"
          >
            <ak-close
              v-if="showX"
              class="ak-modal__close bg-white"
              @click.prevent="cancel('x')"
            />
            <div class="ak-media-manager__body">
              <ak-loading v-model:active="loading" :is-full-page="false" />
              <div class="ak-media-manager__header">
                <ak-pack class="max-width-600">
                  <ak-pack-item>
                    <ak-form @submit.prevent="search">
                      <ak-field class="mb-none">
                        <ak-input
                          v-model="searchQuery"
                          type="search"
                          icon="search"
                          style="width: 350px"
                          @right-icon-click="search"
                        />
                      </ak-field>
                    </ak-form>
                  </ak-pack-item>

                  <ak-pack-item>
                    <ak-dropdown v-model="sortOptions">
                      <template #trigger>
                        <ak-tooltip>
                          <template #trigger>
                            <ak-button ghost color="grey-light">
                              <ak-icon type="funnel-b" size="big" />
                            </ak-button>
                          </template>
                          Sorting Options
                        </ak-tooltip>
                      </template>
                      <ak-dropdown-item value="-details.title">
                        Title DESC
                      </ak-dropdown-item>
                      <ak-dropdown-item value="details.title">
                        Title ASC
                      </ak-dropdown-item>
                      <ak-dropdown-item value="-createdOn">
                        Created On DESC
                      </ak-dropdown-item>
                      <ak-dropdown-item value="createdOn">
                        Created On ASC
                      </ak-dropdown-item>
                    </ak-dropdown>
                  </ak-pack-item>

                  <ak-pack-item v-show="!loading">
                    <ak-tag style="white-space: nowrap"
                      >{{ total }} Image<span v-show="media.length > 1">s</span>
                      &nbsp;found</ak-tag
                    >
                  </ak-pack-item>

                  <ak-pack-item style="margin-left: auto; margin-right: auto">
                    <ak-button-group>
                      <ak-tooltip>
                        <template #trigger>
                          <ak-button
                            :ghost="view !== 'grid'"
                            color="grey-light"
                            @click="setView('grid')"
                          >
                            <ak-icon type="grid" size="big" />
                          </ak-button>
                        </template>
                        Grid View
                      </ak-tooltip>
                      <ak-tooltip>
                        <template #trigger>
                          <ak-button
                            :ghost="view !== 'list'"
                            color="grey-light"
                            @click="setView('list')"
                          >
                            <ak-icon type="list" size="big" />
                          </ak-button>
                        </template>
                        List View
                      </ak-tooltip>
                    </ak-button-group>
                  </ak-pack-item>

                  <ak-pack-item style="margin-left: auto">
                    <form enctype="multipart/form-data" novalidate @drop="drop">
                      <input
                        id="uploadfile"
                        ref="file"
                        class="ak-media-manager__hidden-input"
                        type="file"
                        multiple
                        :name="uploadFieldName"
                        :disabled="isSaving"
                        @change="uploadChanged"
                      />
                      <ak-button
                        tag="label"
                        for="uploadfile"
                        :is-loading="isSaving"
                      >
                        <ak-icon
                          type="android-image"
                          size="big"
                          class="mr-tiny"
                        />
                        Upload New
                      </ak-button>
                    </form>
                  </ak-pack-item>
                </ak-pack>
              </div>

              <div v-show="!initializing" class="ak-media-manager__images">
                <ak-pack-item style="margin-left: auto">
                  <form enctype="multipart/form-data" novalidate @drop="drop">
                    <transition name="fade">
                      <div
                        v-show="isDraggingOver"
                        class="ak-media-manager__overlay"
                      >
                        <ak-empty-state
                          icon="images"
                          title="Upload images"
                          inverse
                        >
                        </ak-empty-state>
                      </div>
                    </transition>
                  </form>
                </ak-pack-item>
                <div class="flex mb-small mr">
                  <ak-tag
                    ghost
                    color="warning"
                    style="margin-left: auto"
                    @click="
                      showSupport(
                        'article',
                        '49909-uploading-images-for-the-web',
                      )
                    "
                  >
                    <ak-icon type="information-circled" class="mr-tiny" />
                    Image Upload Tips
                  </ak-tag>
                </div>

                <ak-infinite-scroll
                  :load="fetchMore"
                  :is-disabled="scrollDisabled"
                  class="ak-media-manager__scroll"
                >
                  <ak-cards
                    v-if="view === 'list'"
                    v-model="selectedMedia"
                    gutter="small"
                  >
                    <ak-cards-item
                      v-for="(loadingItem, $index) in fileCount"
                      v-show="uploading"
                      :key="`loading-${$index}`"
                      style="width: 100%"
                    >
                      <ak-card>
                        <ak-card-body narrow size="small" class="text-overflow">
                          <ak-pack>
                            <ak-pack-item>
                              <ak-loading-spinner size="small" />
                            </ak-pack-item>
                            <ak-pack-item>
                              <strong>Title:</strong>
                              <br />
                              <strong>Alt:</strong>
                              <br />
                              <strong>Filesize:</strong>
                              <br />
                              <strong>Original Size:</strong>
                            </ak-pack-item>
                          </ak-pack>
                        </ak-card-body>
                      </ak-card>
                    </ak-cards-item>

                    <ak-cards-item
                      v-for="(item, idx) in media"
                      :key="idx"
                      style="width: 100%"
                    >
                      <ak-card :value="item" :selectable="true">
                        <ak-card-body narrow size="small" class="text-overflow">
                          <ak-pack>
                            <ak-pack-item>
                              <img
                                style="
                                  width: 150px;
                                  height: 100px;
                                  object-fit: cover;
                                "
                                :src="thumbnail(item.items[0].source, 300)"
                              />
                            </ak-pack-item>
                            <ak-pack-item>
                              <strong>Title:</strong> {{ item.details.title }}
                              <br />
                              <strong>Alt:</strong> {{ item.details.alt }}
                              <br />
                              <strong>Filesize:</strong>
                              {{ formatBytes(item.items[0].filesize) }}
                              <br />
                              <strong>Original Size:</strong>
                              {{ item.details.originalWidth }}x{{
                                item.details.originalHeight
                              }}
                            </ak-pack-item>
                          </ak-pack>
                        </ak-card-body>
                      </ak-card>
                    </ak-cards-item>
                  </ak-cards>

                  <ak-cards
                    v-if="view === 'grid'"
                    v-model="selectedMedia"
                    gutter="small"
                  >
                    <ak-cards-item
                      v-for="(loadingItem, $index) in fileCount"
                      v-show="uploading"
                      :key="`loading-${$index}`"
                      class="one-third desk--one-quarter"
                    >
                      <ak-card>
                        <ak-card-body class="text-center">
                          <ak-loading-spinner size="small" />
                        </ak-card-body>
                      </ak-card>
                    </ak-cards-item>
                    <ak-cards-item
                      v-for="(item, idx) in media"
                      :key="idx"
                      class="one-third desk--one-quarter"
                    >
                      <ak-card :value="item" :selectable="true">
                        <ak-card-image
                          class="h-150"
                          :src="thumbnail(item.items[0].source, 300)"
                        />
                        <ak-card-body narrow size="small" class="text-overflow">
                          {{ ellipsis(item.details.title, 20) }}
                        </ak-card-body>
                      </ak-card>
                    </ak-cards-item>
                  </ak-cards>
                </ak-infinite-scroll>
              </div>
            </div>

            <div class="ak-media-manager__sidebar">
              <transition name="fade" mode="out-in">
                <ak-empty-state
                  v-if="!selectedMedia"
                  title="Please select an image to insert"
                  icon="image"
                />
                <div v-else class="ak-media-manager__sidebar-inner">
                  <div class="ak-media-manager__sidebar-body">
                    <div class="ak-media-manager__sidebar-img">
                      <img
                        :src="thumbnail(selectedMedia.items[0].source, 500)"
                      />
                    </div>
                    <ak-box size="small">
                      <ak-form>
                        <ak-field>
                          <small>
                            <strong>Filesize:</strong>
                            {{ formatBytes(selectedMedia.items[0].filesize) }}
                            <strong>Original Size:</strong>
                            {{ selectedMedia.details.originalWidth }}x{{
                              selectedMedia.details.originalHeight
                            }}
                            <br />
                            <a
                              class="color-danger text-link"
                              @click.prevent="deleteMedia(selectedMedia)"
                            >
                              Delete Permanently
                            </a>
                          </small>
                        </ak-field>

                        <ak-field label="Image Name">
                          <ak-input
                            v-model="selectedMedia.details.title"
                            @blur="updateSelected"
                          />
                        </ak-field>

                        <ak-field label="Image Alt Text">
                          <ak-input
                            v-model="selectedMedia.details.alt"
                            @blur="updateSelected"
                          />
                        </ak-field>

                        <template v-if="hideImageOpts !== true">
                          <ak-field label="Link URL">
                            <ak-input
                              v-model="selectedMedia.items[0].link"
                              @blur="updateSelected"
                            />
                          </ak-field>

                          <ak-field label="Alignment">
                            <ak-select
                              v-model="selectedMedia.details.alignment"
                              :options="alignmentOptions"
                              @select="updateSelected"
                            />
                          </ak-field>

                          <ak-field label="Scale">
                            <ak-select
                              v-model="imageScale"
                              :options="scaleOptions"
                              @select="updateSelected"
                            />
                          </ak-field>

                          <ak-field label="Image Quality">
                            <ak-select
                              v-model="imageQuality"
                              :options="qualityOptions"
                              track-by="name"
                              label="name"
                              @select="setImageQuality"
                            />
                          </ak-field>

                          <ak-field label="Automatic Optimisation">
                            <template #helper>
                              Force your image to be in JPG format. This helps
                              reduce filesize and improve loading time. However,
                              this will remove any transparency and the colour
                              profiles may change. We recommend checking this
                              option unless you see negative effects.
                            </template>
                            <ak-switch
                              v-model="automaticOptimisation"
                              :label="true"
                            >
                              Use automatic optimisation
                            </ak-switch>
                          </ak-field>
                        </template>
                      </ak-form>
                    </ak-box>
                  </div>
                  <ak-button
                    v-if="selectedMedia"
                    class="ak-media-manager__sidebar-btn"
                    size="large"
                    @click.prevent="chooseMedia(selectedMedia)"
                  >
                    Choose Image
                  </ak-button>
                </div>
              </transition>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  modalProps,
  mediaManagerProps,
  modalMixin,
} from '../../mixins/modalMixin'
import { DialogProgrammatic } from '../dialog'
import { ToastProgrammatic } from '../toast'
import trapFocus from '../../directives/trapFocus'
import ModalButton from '../modal/ModalButton.vue'
import Cards from '../cards/Cards.vue'
import CardsItem from '../cards/CardsItem.vue'
import Card from '../cards/Card.vue'
import CardBody from '../cards/CardBody.vue'
import CardImage from '../cards/CardImage.vue'
import Field from '../field/Field.vue'
import Input from '../input/Input.vue'
import Checkbox from '../checkbox/Checkbox.vue'
import Select from '../select/Select.vue'
import Switch from '../switch/Switch.vue'
import Loading from '../loading/Loading.vue'
import Icon from '../icon/Icon.vue'
import Box from '../box/Box.vue'
import Tag from '../tag/Tag.vue'
import ButtonGroup from '../buttonGroup/ButtonGroup.vue'
import Button from '../button/Button.vue'
import Tooltip from '../tooltip/Tooltip.vue'
import Dropdown from '../dropdown/Dropdown.vue'
import DropdownItem from '../dropdown/DropdownItem.vue'
import Close from '../close/Close.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'
import EmptyState from '../emptyState/EmptyState.vue'
import LoadingSpinner from '../loading/LoadingSpinner.vue'
import InfiniteScroll from '../infiniteScroll/InfiniteScroll.vue'
import Form from '../form/Form.vue'
import { get, post, put, remove } from '../../utils/request'
import { cloneObject, asyncForEach, isObject } from '../../utils/helpers'
import { ellipsis } from '../../filters/string'
import { formatBytes } from '../../filters/numbers'

import { defineComponent, ref, computed, onMounted, watch, nextTick } from 'vue'

const STATUS_INITIAL = 0
const STATUS_SAVING = 1
const STATUS_SUCCESS = 2
const STATUS_FAILED = 3

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-media-manager />
   */
  name: 'AkMediaManager',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [InfiniteScroll.name]: InfiniteScroll,
    [ModalButton.name]: ModalButton,
    [CardsItem.name]: CardsItem,
    [CardBody.name]: CardBody,
    [Cards.name]: Cards,
    [Card.name]: Card,
    [CardImage.name]: CardImage,
    [Field.name]: Field,
    [Input.name]: Input,
    [Checkbox.name]: Checkbox,
    [Select.name]: Select,
    [Switch.name]: Switch,
    [Loading.name]: Loading,
    [Icon.name]: Icon,
    [Box.name]: Box,
    [Tag.name]: Tag,
    [Button.name]: Button,
    [ButtonGroup.name]: ButtonGroup,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Close.name]: Close,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [EmptyState.name]: EmptyState,
    [LoadingSpinner.name]: LoadingSpinner,
    [Form.name]: Form,
  },

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    trapFocus,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...modalProps,
    ...mediaManagerProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close', 'update:active'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes method for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const root = ref(null)
    const file = ref(null)
    const view = ref('grid')
    const initializing = ref(true)
    const selectedMedia = ref(null)
    const automaticOptimisation = ref(true)
    const imageQuality = ref({ name: '90%', value: 90 })
    const imageAlignment = ref('left')
    const imageScale = ref('fit')
    const fileCount = ref(0)
    const uploadFieldName = ref('uploads')
    const uploadedFiles = ref([])
    const uploadError = ref(null)
    const searchQuery = ref('')
    const currentStatus = ref(STATUS_INITIAL)
    const currentPage = ref(1)
    const perPage = ref(48)
    const total = ref(0)
    const scrollDisabled = ref(true)
    const alignmentOptions = ref(['left', 'center', 'right'])
    const scaleOptions = ref(['original', 'crop', 'fit', 'scale'])
    const media = ref([])
    const loading = ref(true)
    const isDraggingOver = ref(false)
    const uploading = ref(false)
    const sortOptions = ref('-createdOn')

    const accountId = computed(() =>
      isObject(props.account) ? props.account.id : props.account,
    )

    const agencyId = computed(() =>
      isObject(props.agency) ? props.agency.id : props.agency,
    )

    const qualityOptions = ref([
      { name: '10%', value: 10 },
      { name: '20%', value: 20 },
      { name: '30%', value: 30 },
      { name: '40%', value: 40 },
      { name: '50%', value: 50 },
      { name: '60%', value: 60 },
      { name: '70%', value: 70 },
      { name: '80%', value: 80 },
      { name: '90%', value: 90 },
      { name: '100%', value: 100 },
    ])

    const mixin = modalMixin({
      type: 'mediaManager',
      props,
      emit,
      attrs,
      refs: {
        root,
      },
    })

    const isInitial = computed(() => {
      return currentStatus.value === STATUS_INITIAL
    })

    const isSaving = computed(() => {
      return currentStatus.value === STATUS_SAVING
    })

    const isSuccess = computed(() => {
      return currentStatus.value === STATUS_SUCCESS
    })

    const isFailed = computed(() => {
      return currentStatus.value === STATUS_FAILED
    })

    const config = computed(() => {
      return {
        index: props.index,
        callback: props.callback,
        scopeId: props.scopeId,
        hideImageOpts: props.hideImageOpts,
        hide: props.hide,
        ng: props.ng,
        single: true,
        debug: false,
      }
    })

    /**
     * @description Resets the upload status
     */
    const reset = () => {
      currentStatus.value = STATUS_INITIAL
      uploadedFiles.value = []
      uploadError.value = null
    }

    /**
     * @returns {String}
     * @description Returns the sort order for the API requests
     */
    const getSortOrder = () => {
      return sortOptions.value || '-createdOn'
    }

    /**
     * @description Checks to see if the infinate scroller should
     * be disabled and disables it if needed
     */
    const checkDisabledScroller = () => {
      if (media.value.length < perPage.value) {
        scrollDisabled.value = true
      } else {
        scrollDisabled.value = false
      }
    }

    /**
     * @returns {Promise}
     * @description Sets the search query parameter and then triggers the fetch
     * method to pull the filtered media items from the API
     */
    const search = async () => {
      loading.value = true
      media.value = []
      currentPage.value = 1
      selectedMedia.value = null

      const { data: response } = await fetchMedia()
      const { data, meta } = response
      const { pagination } = meta
      const { total: totalMedia } = pagination

      media.value = media.value.concat(data)
      total.value = totalMedia
      loading.value = false

      checkDisabledScroller()
    }

    /**
     * @returns {Promise}
     * @description Tries to fetch the media items from the API
     */
    const fetchMedia = async () => {
      const search =
        searchQuery.value.length > 0 ? searchQuery.value : undefined

      const params = {
        'details.type': 'image',
        'details.account': accountId.value,
        agency: agencyId.value,
        sort: getSortOrder(),
        per_page: perPage.value,
        page: currentPage.value,
        cacheBuster: Date.now(),
      }

      if (search) {
        params.blacklist = 'details.account, details.type'
        params.regex = true
        params.orSearch = true
        params['details.title'] = search
        params['details.alt'] = search
      }

      return await get('/media', { params })
    }

    /**
     * @returns {Promise}
     * @description increment's the current page and then tries to fetch the
     * next page of the media items from the API
     */
    const fetchMore = async () => {
      currentPage.value += 1
      const { data: response } = await fetchMedia()
      const { data } = response
      media.value = media.value.concat(data)
      checkDisabledScroller()
    }

    /**
     * @param {String} name - The name of the view to swap too.
     * @description Sets the requested image view.
     */
    const setView = async (name = 'grid') => {
      if (typeof name !== 'string' || name.length === 0) return
      const [scroll] = document.querySelectorAll('.ak-media-manager__scroll')
      view.value = name
      nextTick(() => {
        if (selectedMedia.value !== null) {
          const [selected] = document.querySelectorAll('.is-selected')
          if (selected) {
            scroll.scrollTop = selected.offsetTop
          }
        } else {
          scroll.scrollTop = 0
        }
      })
    }

    /**
     * @param {String} src - The path for the current image
     * @param {Number} width - width of the image to return
     * @description Generate a thumbnail size of the image
     */
    const thumbnail = (src, width = 300) => {
      return src.replace('/upload/', `/upload/f_auto,w_${width},q_50,c_fill/`)
    }

    /**
     * @description Triggered but the options inputs for the selected media
     * item in the sidebar, this will make a request to the API to update
     * the media item via the API.
     */
    const updateSelected = async () => {
      const updated = { ...selectedMedia.value }
      delete updated._id
      delete updated.__v
      await put(`/media/${selectedMedia.value._id}`, updated)
    }

    /**
     * @description Simple method for calling a callback method but
     * always catch the error thrown
     */
    const handleError = (fn, args = []) => {
      try {
        fn.apply(null, args)
      } catch (e) {
        console.log(e)
      }
    }

    /**
     * @param {Object} selectedMedia
     * @description Chooses the selected media item and then triggers the
     * callback to return the data to the client that opened the media
     * manager
     */
    const chooseMedia = (selectedMedia) => {
      handleError(() => {
        const result = cloneObject(selectedMedia)
        result.items[0].base = result.items[0].source
        result.alt = result.details.alt
        result.imgalt = result.alt
        props.callback(result, config.value)
        mixin.cancel('x')
      })
    }

    /**
     * @param {Object} media
     * @description Triggers a delete confirmation for removing the
     * selected media item via the API, on confirm we trigger the
     * API request to remove the media
     */
    const deleteMedia = (selected) => {
      DialogProgrammatic.confirm({
        title: 'Deleting media',
        message:
          'Are you sure you want to <b>delete</b> the media item? This action cannot be undone.',
        confirmText: 'Delete Media',
        type: 'danger',
        hasIcon: true,
        onConfirm: () => {
          remove(`/media?includes=${selected._id}`).then(() => {
            media.value = media.value.filter((i) => i._id !== selected._id)
            selectedMedia.value = null
            total.value -= 1
          })
        },
      })
    }

    /**
     * @param {fileName} string
     * @param {fileData} String
     * @returns {Promise}
     * @description Uploads a media item via the API
     */
    const upload = (fileName, fileData) => {
      return post('/media', {
        account: accountId.value,
        agency: agencyId.value,
        v2: true,
        fileName,
        fileData,
      })
    }

    /**
     * @param {File} fileData
     * @returns {Promise}
     * @description Returns a promise to return the Base64 representation of
     * the image to be uploaded
     */
    const createBase64Image = (fileData) => {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.addEventListener('load', (e) => resolve(e.target.result))
        reader.readAsDataURL(fileData)
      })
    }

    /**
     * @param {Object}
     * @description Sets the image quality of the selected media item
     */
    const setImageQuality = ({ value }) => {
      handleError(() => {
        selectedMedia.value.details.quality = value
        updateSelected()
      })
    }

    /**
     * @description Show a drag over overlay when a file is dragged over
     */
    const dragover = ($event) => {
      $event.preventDefault()
      isDraggingOver.value = true
    }

    /**
     * @description Hide the drag over overlay when a file is dragged off
     */
    const dragleave = () => {
      isDraggingOver.value = false
    }

    /**
     * @description Handle the drop of a file that's dragged over and hide overlay
     */
    const drop = ($event) => {
      const files = $event.dataTransfer.files
      $event.preventDefault()
      filesChange(file.value.name, files)
      fileCount.value = files.length
      isDraggingOver.value = false
    }

    const uploadChanged = async ($event) => {
      fileCount.value = $event.target.files.length
      await filesChange($event.target.name, $event.target.files)
    }

    /**
     * @param {String} fieldName
     * @param {FileList} fileList
     * @description Callback for the upload input, this will trigger an upload
     * for each of the given images in the FileList
     */
    const filesChange = async (fieldName, fileList) => {
      if (!fileList.length) return
      uploading.value = true
      const keys = Array.from(Array(fileList.length).keys())

      try {
        await asyncForEach(keys, async (index) => {
          const file = fileList[index]
          const size = file.size / 1024 / 1024
          if (size > 2) {
            throw new Error('File size is too big, max size is 2MB')
          }
        })

        await asyncForEach(keys, async (index) => {
          const file = fileList[index]
          const base64 = await createBase64Image(file)
          const { data: result } = await upload(file.name, base64)
          const { data } = result
          media.value.unshift(data)
          // lower the file count since we've uploaded one and it is now in the
          // main list of files - it also removes a loading tile
          fileCount.value--
          total.value += 1
        })
      } catch (ex) {
        ToastProgrammatic.open({
          duration: 10000,
          message: ex.message,
          type: 'danger',
        })
      }

      fileCount.value = 0
      uploading.value = false
      file.value.value = ''
    }

    onMounted(async () => {
      try {
        reset()
        const { data: response } = await fetchMedia()
        const { data, meta } = response
        const { pagination } = meta
        const { total: totalMedia } = pagination
        scrollDisabled.value = totalMedia < perPage.value
        total.value = totalMedia
        media.value = data
      } catch (ex) {
        console.error(ex)
      } finally {
        loading.value = false
        initializing.value = false
      }
    })

    watch(sortOptions, () => {
      search()
    })

    return {
      ...mixin,
      initializing,
      root,
      file,
      view,
      selectedMedia,
      automaticOptimisation,
      imageQuality,
      imageAlignment,
      imageScale,
      fileCount,
      uploadFieldName,
      uploadedFiles,
      uploadError,
      searchQuery,
      currentStatus,
      currentPage,
      perPage,
      total,
      scrollDisabled,
      alignmentOptions,
      scaleOptions,
      qualityOptions,
      media,
      loading,
      isDraggingOver,
      uploading,
      sortOptions,
      isInitial,
      isSaving,
      isSuccess,
      isFailed,
      config,
      ellipsis,
      formatBytes,
      reset,
      fetchMedia,
      search,
      checkDisabledScroller,
      setView,
      thumbnail,
      updateSelected,
      fetchMore,
      handleError,
      chooseMedia,
      deleteMedia,
      upload,
      createBase64Image,
      setImageQuality,
      dragover,
      dragleave,
      drop,
      filesChange,
      uploadChanged,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';

.ak-media-manager {
  padding: $spacing-small;

  &__dialog {
    width: 100%;
    max-width: none;
    height: 100%;
    display: flex;
    flex-direction: row;
    position: relative;
  }

  &__body {
    flex-grow: 1;
    height: 100%;
    position: relative;
  }

  &__images {
    overflow-y: scroll;
    scroll-behavior: smooth;
    position: absolute;
    top: 75px;
    left: 0px;
    bottom: 0px;
    right: 0px;

    img {
      pointer-events: none;
      user-drag: none;
    }
  }

  &__scroll {
    padding-left: $spacing;
    padding-right: $spacing;
  }

  &__header {
    padding: $spacing $spacing $spacing-small;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 999;
    background: white;
    width: 100%;
  }

  &__overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 5;
    background-color: rgba($color-primary, 0.75);
    backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
  }

  &__sidebar {
    flex-basis: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    border-left: $base-border;

    &-inner {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    &-body {
      flex-grow: 1;
      overflow-y: scroll;
    }

    &-img {
      min-height: 200px;
      display: flex;
      align-items: center;
      background-color: $color-grey-lightest;
    }

    &-btn {
      flex-shrink: 0;
      border-radius: 0;
    }
  }

  &__hidden-input {
    opacity: 0;
    width: 0;
    position: absolute;
  }
}
</style>
